import { useEffect, useMemo, useRef, useState } from 'react'
import { useNavigate, useParams } from 'react-router-dom'
import { loadStripe, type Stripe, type StripeCardNumberElementOptions } from '@stripe/stripe-js'
import {
  CardNumberElement, CardExpiryElement, CardCvcElement,
  Elements, useElements, useStripe,
} from '@stripe/react-stripe-js'
import { createPaymentIntent, fetchPaymentMethods, getPayment, getOrder } from '../lib/api.js'
import { useSessionStore } from '../stores/session.store.js'
import { formatMoney } from '../lib/money.js'
import { NintaiGOHeader } from '../components/NintaiGOHeader.js'
import { GuestTipSelector } from '../components/GuestTipSelector.js'

/**
 * Cobro digital del comensal (Stripe.js). Flujo: PROPINA → TARJETA → RECIBO.
 * Sólo lo alcanza TAKEOUT + Pago en línea. Propina: reusa el TipSelector del POS
 * (comensal/barra) → cobro = consumo + propina (server-validado, tope 100%). 3DS
 * vía confirmCardPayment. El RECIBO sondea hasta que el WEBHOOK confirme CAPTURED.
 */

const TICKETS_BASE = 'https://tickets.nintaigo.com'

const stripeCache = new Map<string, Promise<Stripe | null>>()
function stripeFor(pk: string): Promise<Stripe | null> {
  let p = stripeCache.get(pk)
  if (!p) { p = loadStripe(pk); stripeCache.set(pk, p) }
  return p
}

const FIELD_STYLE: StripeCardNumberElementOptions['style'] = {
  base: {
    color: '#1D1D1F',
    fontFamily: "system-ui, -apple-system, BlinkMacSystemFont, 'Inter', 'Segoe UI', Roboto, sans-serif",
    fontSize: '16px', fontSmoothing: 'antialiased',
    '::placeholder': { color: 'rgba(60, 60, 67, 0.42)' }, iconColor: '#1F8B3A',
  },
  invalid: { color: '#C9302C', iconColor: '#C9302C' },
}

export function PaymentOnline() {
  const { slug, orderId } = useParams<{ slug: string; orderId: string }>()
  const navigate = useNavigate()
  const session = useSessionStore((s) => s.session)
  const token = session?.token

  const [step, setStep] = useState<'tip' | 'card' | 'receipt'>('tip')
  const [baseCents, setBaseCents] = useState<number | null>(null)   // consumo (total de la orden)
  const [tipCents, setTipCents] = useState(0)
  const [error, setError] = useState<string | null>(null)
  // Datos del intent (tras crear con la propina):
  const [clientSecret, setClientSecret] = useState<string | null>(null)
  const [pk, setPk] = useState<string | null>(null)
  const [paymentId, setPaymentId] = useState<string | null>(null)
  const [totals, setTotals] = useState<{ subtotal: number; tip: number; total: number } | null>(null)
  const [creating, setCreating] = useState(false)
  // Presets de propina del negocio (gate.tipPresets); null = usar default del selector.
  const [tipPresets, setTipPresets] = useState<number[] | null>(null)
  const loadedRef = useRef(false)

  // 1) Cargar el consumo (base de la propina) — sin crear intent aún. De paso los
  //    presets de propina configurados por el negocio (best-effort: default si falla).
  useEffect(() => {
    if (!token || !orderId || loadedRef.current) return
    loadedRef.current = true
    void (async () => {
      const r = await getOrder(token, orderId)
      if (!r.ok) { setError(`No se pudo cargar la orden: ${r.error.message}`); return }
      const due = r.data.totalCents - (r.data.paidCents ?? 0)
      setBaseCents(Math.max(0, due))
      const g = await fetchPaymentMethods(token)
      if (g.ok && (g.data.methods?.tipPresets?.length ?? 0) > 0) setTipPresets(g.data.methods!.tipPresets!)
    })()
  }, [token, orderId])

  const stripePromise = useMemo(() => (pk ? stripeFor(pk) : null), [pk])

  if (!session) { navigate(`/r/${slug}`, { replace: true }); return <></> }

  async function continueToCard(): Promise<void> {
    if (!token || !orderId) return
    setCreating(true); setError(null)
    const r = await createPaymentIntent(token, orderId, tipCents)
    setCreating(false)
    if (!r.ok) { setError(`No se pudo iniciar el pago: ${r.error.code} — ${r.error.message}`); return }
    if (!r.data.clientSecret || !r.data.publishableKey) {
      setError('El negocio no tiene el pago en línea configurado por completo.'); return
    }
    setClientSecret(r.data.clientSecret); setPk(r.data.publishableKey); setPaymentId(r.data.paymentId)
    setTotals({ subtotal: r.data.subtotalCents, tip: r.data.tipCents, total: r.data.amountCents })
    setStep('card')
  }

  // ── RECIBO ──────────────────────────────────────────────────────────────
  if (step === 'receipt' && paymentId) {
    return (
      <Receipt slug={slug!} orderId={orderId!} paymentId={paymentId} token={token!}
        totals={totals} tableCode={session.tableCode} customerName={session.customerName} />
    )
  }

  return (
    <div className="app">
      <NintaiGOHeader slug={slug} tableCode={session.tableCode} customerName={session.customerName} branchId={session.branchId} />
      <div className="container">
        <button className="btn btn-ghost" style={{ width: 'auto', marginBottom: 12 }}
          onClick={() => (step === 'card' ? setStep('tip') : navigate(`/r/${slug}/cart`))}>← Volver</button>

        {error && <div className="card"><div className="danger" style={{ fontSize: 13 }}>{error}</div></div>}

        {/* ── PASO 1: PROPINA ── */}
        {step === 'tip' && (
          <div className="card">
            <h2 style={{ marginTop: 0, marginBottom: 4 }}>¿Deseas dejar propina?</h2>
            <p className="muted" style={{ marginTop: 0, marginBottom: 12 }}>Es opcional. Puedes elegir un porcentaje o un monto.</p>
            {baseCents === null ? (
              <div className="muted"><span className="spinner" /> Cargando…</div>
            ) : (
              <>
                <GuestTipSelector baseCents={baseCents} valueCents={tipCents} onChange={setTipCents} {...(tipPresets ? { presets: tipPresets } : {})} />
                <div style={{ marginTop: 16, borderTop: '1px solid var(--line)', paddingTop: 12, display: 'flex', flexDirection: 'column', gap: 6 }}>
                  <Row k="Consumo" v={formatMoney(baseCents)} />
                  <Row k="Propina" v={formatMoney(tipCents)} />
                  <Row k="Total" v={formatMoney(baseCents + tipCents)} strong />
                </div>
                <button className="btn btn-primary" style={{ marginTop: 14 }} onClick={continueToCard} disabled={creating}>
                  {creating ? (<><span className="spinner" /> Preparando…</>) : `Continuar · ${formatMoney(baseCents + tipCents)}`}
                </button>
              </>
            )}
          </div>
        )}

        {/* ── PASO 2: TARJETA ── */}
        {step === 'card' && clientSecret && stripePromise && totals && (
          <div className="card">
            <h2 style={{ marginTop: 0, marginBottom: 4 }}>Pago con tarjeta</h2>
            <div style={{ marginBottom: 6, display: 'flex', flexDirection: 'column', gap: 4 }}>
              <Row k="Consumo" v={formatMoney(totals.subtotal)} />
              {totals.tip > 0 && <Row k="Propina" v={formatMoney(totals.tip)} />}
              <Row k="Total a pagar" v={formatMoney(totals.total)} strong />
            </div>
            <Elements stripe={stripePromise}>
              <CardForm clientSecret={clientSecret} amountCents={totals.total} onPaid={() => setStep('receipt')} />
            </Elements>
          </div>
        )}

        <p className="muted" style={{ textAlign: 'center', marginTop: 12, display: 'flex', gap: 6, justifyContent: 'center', alignItems: 'center' }}>
          <span aria-hidden>🔒</span> Pago protegido con 3D Secure · procesado por Stripe
        </p>
      </div>
    </div>
  )
}

function FieldBox({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <label style={{ display: 'block' }}>
      <span style={{ display: 'block', fontSize: 13, fontWeight: 600, color: 'var(--fg-muted)', marginBottom: 6 }}>{label}</span>
      <div style={{ background: '#FFFFFF', border: '1px solid var(--line-strong)', borderRadius: 'var(--radius-input, 14px)', padding: '13px 14px' }}>{children}</div>
    </label>
  )
}

function CardForm({ clientSecret, amountCents, onPaid }: { clientSecret: string; amountCents: number; onPaid: () => void }) {
  const stripe = useStripe()
  const elements = useElements()
  const [paying, setPaying] = useState(false)
  const [err, setErr] = useState<string | null>(null)
  const [name, setName] = useState('')
  const [email, setEmail] = useState('')
  const canPay = !!stripe && !!elements && name.trim().length > 1 && /.+@.+\..+/.test(email) && !paying

  async function pay(): Promise<void> {
    if (!stripe || !elements) return
    const card = elements.getElement(CardNumberElement)
    if (!card) return
    setPaying(true); setErr(null)
    const { error, paymentIntent } = await stripe.confirmCardPayment(clientSecret, {
      payment_method: { card, billing_details: { name: name.trim(), email: email.trim() } },
      receipt_email: email.trim(),
    })
    if (error) { setErr(error.message ?? 'No se pudo procesar el pago. Revisa tu tarjeta.'); setPaying(false); return }
    if (paymentIntent && (paymentIntent.status === 'succeeded' || paymentIntent.status === 'processing')) { onPaid(); return }
    setErr('El pago quedó pendiente. Si se te cobró, avísanos.'); setPaying(false)
  }

  const nativeInput: React.CSSProperties = { width: '100%', border: 'none', outline: 'none', background: 'transparent', font: 'inherit', color: '#1D1D1F' }

  return (
    <div style={{ marginTop: 14, display: 'flex', flexDirection: 'column', gap: 12 }}>
      <FieldBox label="Nombre en la tarjeta"><input style={nativeInput} value={name} onChange={(e) => setName(e.target.value)} placeholder="Nombre y apellido" autoComplete="cc-name" /></FieldBox>
      <FieldBox label="Correo (para tu recibo)"><input style={nativeInput} value={email} onChange={(e) => setEmail(e.target.value)} placeholder="tu@correo.com" type="email" autoComplete="email" inputMode="email" /></FieldBox>
      <FieldBox label="Número de tarjeta"><CardNumberElement options={{ style: FIELD_STYLE, placeholder: '1234 1234 1234 1234' }} /></FieldBox>
      <div style={{ display: 'flex', gap: 12 }}>
        <div style={{ flex: 1 }}><FieldBox label="Vence"><CardExpiryElement options={{ style: FIELD_STYLE }} /></FieldBox></div>
        <div style={{ flex: 1 }}><FieldBox label="CVC"><CardCvcElement options={{ style: FIELD_STYLE }} /></FieldBox></div>
      </div>
      {err && <div className="danger" style={{ fontSize: 13 }}>{err}</div>}
      <button className="btn btn-primary" style={{ marginTop: 2 }} onClick={pay} disabled={!canPay}>
        {paying ? (<><span className="spinner" /> Procesando…</>) : `Pagar ${formatMoney(amountCents)}`}
      </button>
    </div>
  )
}

function Receipt({ slug, orderId, paymentId, token, totals, tableCode, customerName }: {
  slug: string; orderId: string; paymentId: string; token: string
  totals: { subtotal: number; tip: number; total: number } | null
  tableCode?: string; customerName?: string
}) {
  const navigate = useNavigate()
  const [status, setStatus] = useState('PENDING')
  const [brand, setBrand] = useState<string | undefined>()
  const [last4, setLast4] = useState<string | undefined>()
  const [receiptUrl, setReceiptUrl] = useState<string | undefined>()
  const [ticketUuid, setTicketUuid] = useState<string | undefined>()
  const [pickupCode, setPickupCode] = useState<string | undefined>()

  useEffect(() => {
    let alive = true, tries = 0
    const tick = async () => {
      if (!alive) return
      const [pr, or] = await Promise.all([getPayment(token, paymentId), getOrder(token, orderId)])
      if (alive && pr.ok) {
        setStatus(pr.data.status); setBrand(pr.data.evidence?.cardBrand)
        setLast4(pr.data.evidence?.cardLast4); setReceiptUrl(pr.data.evidence?.receiptUrl)
      }
      if (alive && or.ok && or.data.publicTicketUuid) setTicketUuid(or.data.publicTicketUuid)
      if (alive && or.ok && or.data.pickupCode) setPickupCode(or.data.pickupCode)
      const done = pr.ok && (pr.data.status === 'CAPTURED' || pr.data.status === 'FAILED')
      tries += 1
      // Ventana de sondeo ~90s: la captura depende del webhook de Stripe, que tras
      // 3DS / reintento de cargo puede tardar 30-60s. Con 15×2s=30s la pantalla se
      // rendía ("Confirmando…" clavado) aunque el back ya hubiera capturado.
      if (alive && !done && tries < 45) setTimeout(() => void tick(), 2000)
    }
    void tick()
    return () => { alive = false }
  }, [token, paymentId, orderId])

  const confirmed = status === 'CAPTURED'

  return (
    <div className="app">
      <NintaiGOHeader slug={slug} tableCode={tableCode} customerName={customerName} />
      <div className="container">
        <div className="card" style={{ textAlign: 'center' }}>
          <div style={{ display: 'flex', justifyContent: 'center', marginBottom: 10 }} aria-hidden>
            {confirmed ? (
              <svg width="60" height="60" viewBox="0 0 24 24" fill="none" role="img" aria-label="Pago confirmado">
                <circle cx="12" cy="12" r="11" fill="#1F8B3A" />
                <path d="M6.8 12.4l3.3 3.3L17.4 8.4" stroke="#FFFFFF" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round" />
              </svg>
            ) : (<span className="spinner" style={{ width: 40, height: 40, borderWidth: 3 }} />)}
          </div>
          <h2 style={{ margin: '0 0 4px' }}>{confirmed ? '¡Pago recibido!' : 'Confirmando tu pago…'}</h2>
          <p className="muted" style={{ marginTop: 0 }}>
            {confirmed ? 'Tu pedido para llevar ya está en marcha.' : 'Verificando el cargo con el banco. No cierres esta pantalla.'}
          </p>

          <div style={{ textAlign: 'left', marginTop: 16, borderTop: '1px solid var(--line)', paddingTop: 14, display: 'flex', flexDirection: 'column', gap: 8 }}>
            {totals && <Row k="Consumo" v={formatMoney(totals.subtotal)} />}
            {totals && totals.tip > 0 && <Row k="Propina" v={formatMoney(totals.tip)} />}
            <Row k="Total pagado" v={totals ? formatMoney(totals.total) : '—'} strong />
            {pickupCode && <Row k="Código de recolección" v={pickupCode} strong />}
            {(brand || last4) && <Row k="Tarjeta" v={`${brand ? brand.toUpperCase() : 'Tarjeta'} ${last4 ? `•••• ${last4}` : ''}`.trim()} />}
            <Row k="Estado" v={confirmed ? 'Pagado' : 'Procesando…'} />
          </div>

          {/* El pedido ya está pagado: lo ÚNICO que el comensal necesita conservar
              es este código — es lo que el staff valida para entregárselo. */}
          {pickupCode && confirmed && (
            <div className="card join-card" style={{ marginTop: 14 }}>
              <div className="join-eyebrow">Tu código de recolección</div>
              <div className="join-code" aria-label="Código de recolección">{pickupCode}</div>
              <p className="join-hint">
                <strong>Toma una captura.</strong> Enséñalo al recoger: con él
                confirmamos que el pedido es tuyo.
              </p>
            </div>
          )}

          {ticketUuid && (
            <a className="btn btn-ghost" href={`${TICKETS_BASE}/${ticketUuid}`} target="_blank" rel="noopener noreferrer" style={{ marginTop: 14, width: 'auto' }}>Ver / imprimir mi ticket</a>
          )}
          {receiptUrl && (
            <a className="btn btn-ghost" href={receiptUrl} target="_blank" rel="noopener noreferrer" style={{ marginTop: 8, width: 'auto' }}>Recibo de Stripe</a>
          )}
          <button className="btn btn-primary" style={{ marginTop: 10 }} onClick={() => navigate(`/r/${slug}/order/${orderId}`, { replace: true })}>Ver mi pedido</button>
        </div>
        <p className="muted" style={{ textAlign: 'center', marginTop: 12 }}>Guardamos la evidencia de este cargo para tu protección y la del negocio.</p>
      </div>
    </div>
  )
}

function Row({ k, v, strong }: { k: string; v: string; strong?: boolean }) {
  return (
    <div style={{ display: 'flex', justifyContent: 'space-between', gap: 12 }}>
      <span className="muted">{k}</span>
      <span style={{ fontWeight: strong ? 700 : 500 }}>{v}</span>
    </div>
  )
}
